import type { Channel, MuteLevel } from '../../types/index.js';
import type { RequestFn } from '../http.js';

/** Guild channel CRUD + reordering. */
export function makeChannelsApi(request: RequestFn) {
  return {
    listChannels: (guildId: string) =>
      request<{ channels: Channel[] }>(`/api/guilds/${guildId}/channels`),
    createChannel: (
      guildId: string,
      input: {
        type: 'TEXT' | 'VOICE' | 'CATEGORY' | 'ANNOUNCE';
        name: string;
        topic?: string;
        parentId?: string;
        position?: number;
        bitrateKbps?: number;
        userLimit?: number;
        slowmodeS?: number;
        tournament?: boolean;
        color?: string;
      },
    ) =>
      request<Channel>(`/api/guilds/${guildId}/channels`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateChannel: (
      id: string,
      input: {
        name?: string;
        topic?: string | null;
        position?: number;
        parentId?: string | null;
        bitrateKbps?: number | null;
        userLimit?: number | null;
        slowmodeS?: number | null;
        commentsEnabled?: boolean;
        tournament?: boolean;
        color?: string | null;
      },
    ) =>
      request<Channel>(`/api/channels/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    deleteChannel: (id: string) => request<void>(`/api/channels/${id}`, { method: 'DELETE' }),
    /** Whether this tournament room's on-air notice has been accepted by me. */
    getBroadcastConsent: (channelId: string) =>
      request<{ tournament: boolean; version: number; accepted: boolean }>(
        `/api/channels/${channelId}/broadcast-consent`,
      ),
    /** Accept the on-air notice for this room. `version` is the wording that was
     *  actually shown — the server refuses anything but the current one. */
    acceptBroadcastConsent: (channelId: string, version: number) =>
      request<{ ok: true; version: number }>(`/api/channels/${channelId}/broadcast-consent`, {
        method: 'POST',
        body: JSON.stringify({ version }),
      }),
    /** Withdraw it. Leaves the room too, if I am in it. */
    revokeBroadcastConsent: (channelId: string) =>
      request<void>(`/api/channels/${channelId}/broadcast-consent`, { method: 'DELETE' }),
    /** Set this channel's notification level. ALL is stored too: it overrides a
     *  muted server. `mutedUntil` is an ISO string for a timed mute, or
     *  null/omitted for a permanent one. */
    setChannelMute: (channelId: string, level: MuteLevel, mutedUntil?: string | null) =>
      request<{ channelId: string; level: MuteLevel; mutedUntil: string | null }>(
        `/api/channels/${channelId}/mute`,
        { method: 'PUT', body: JSON.stringify({ level, mutedUntil: mutedUntil ?? null }) },
      ),
    reorderChannels: (
      guildId: string,
      orders: { id: string; position: number; parentId?: string | null }[],
    ) =>
      request<void>(`/api/guilds/${guildId}/channels/reorder`, {
        method: 'POST',
        body: JSON.stringify({ orders }),
      }),
  };
}
